import { useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Pagination } from '@/components/UI/Pagination';
import { Alert } from '@/components/UI/Alert';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import {
  useListSchedulerEventsQuery,
  useGetSchedulerEventQuery,
  useCancelSchedulerEventMutation,
  useSyncSchedulerEventsMutation,
  type SchedulerEvent,
} from '@/redux/internal-services/schedulerEventsApi';

const STATUSES = ['active', 'fired', 'cancelled', 'deleted', 'failed'] as const;

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  active: 'accent',
  fired: 'success',
  cancelled: 'neutral',
  deleted: 'neutral',
  failed: 'danger',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'neutral'} className="capitalize">
      {status}
    </Badge>
  );
}

export default function SchedulerEventsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);
  const limit = 25;

  const { data, isFetching, refetch } = useListSchedulerEventsQuery({
    status: statusFilter || undefined,
    event_type: eventTypeFilter || undefined,
    tenant_id: tenantFilter || undefined,
    limit,
    skip,
  });
  const { data: detail } = useGetSchedulerEventQuery(selectedName!, { skip: !selectedName });

  const [cancelEvent, { isLoading: isCancelling }] = useCancelSchedulerEventMutation();
  const [syncEvents, { isLoading: isSyncing }] = useSyncSchedulerEventsMutation();

  const events = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleCancel = async (scheduleName: string) => {
    if (!window.confirm(`Cancel trigger "${scheduleName}"? This removes it from EventBridge and cannot be undone.`)) {
      return;
    }
    try {
      await cancelEvent(scheduleName).unwrap();
    } catch {
      // error surfaced via row still showing active status
    }
  };

  const handleSync = async () => {
    setSyncSummary(null);
    try {
      const res = await syncEvents().unwrap();
      const { eventbridge_active_count, local_active_count, orphaned_marked_deleted } = res.data;
      setSyncSummary(
        `EventBridge: ${eventbridge_active_count} active · Local: ${local_active_count} active` +
          (orphaned_marked_deleted.length ? ` · ${orphaned_marked_deleted.length} orphaned record(s) marked deleted` : ' · in sync'),
      );
    } catch {
      setSyncSummary('Sync failed — check server logs.');
    }
  };

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="Scheduled Triggers"
          subtitle="Track every EventBridge schedule the system has created — reminders, recurring jobs — and cancel active triggers."
          actions={
            <>
              <Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
                {isSyncing ? 'Syncing…' : 'Sync with EventBridge'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? 'Refreshing...' : 'Refresh'}
              </Button>
            </>
          }
          toolbar={
            <div className="flex flex-wrap items-center gap-3">
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
                className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Filter by event type..."
                aria-label="Filter by event type"
                value={eventTypeFilter}
                onChange={(e) => { setEventTypeFilter(e.target.value); setSkip(0); }}
                className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
              />
              <input
                type="text"
                placeholder="Filter by tenant ID..."
                aria-label="Filter by tenant ID"
                value={tenantFilter}
                onChange={(e) => { setTenantFilter(e.target.value); setSkip(0); }}
                className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
              />
              <span className="text-xs text-ui-text3">{total} results</span>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        {syncSummary && <Alert tone="info">{syncSummary}</Alert>}

        {/* Table */}
        <div className="table-card">
          {isFetching ? (
            <LoadingState label="Loading..." />
          ) : events.length === 0 ? (
            <EmptyState title="No scheduled triggers found." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="th-cell">Schedule Name</th>
                    <th className="th-cell">Event Type</th>
                    <th className="th-cell">Status</th>
                    <th className="th-cell">Tenant</th>
                    <th className="th-cell">Fires At</th>
                    <th className="th-cell">Created</th>
                    <th className="th-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e: SchedulerEvent) => (
                    <tr key={e.schedule_name} className="transition-colors hover:bg-ui-hover">
                      <td className="td-cell font-mono text-xs">{e.schedule_name}</td>
                      <td className="td-cell text-xs text-ui-text2">{e.event_type}</td>
                      <td className="td-cell"><StatusBadge status={e.status} /></td>
                      <td className="td-cell text-xs text-ui-text3">{e.tenant_id || '—'}</td>
                      <td className="td-cell text-xs text-ui-text2">
                        {e.fire_at ? new Date(e.fire_at).toLocaleString() : '—'}
                      </td>
                      <td className="td-cell text-xs text-ui-text3">
                        {e.created_on ? new Date(e.created_on).toLocaleString() : '—'}
                      </td>
                      <td className="td-cell">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="link"
                            size="xs"
                            className="text-accent-text"
                            onClick={() => setSelectedName(e.schedule_name === selectedName ? null : e.schedule_name)}
                          >
                            Detail
                          </Button>
                          {e.status === 'active' && (
                            <Button
                              variant="link"
                              size="xs"
                              className="text-danger-text"
                              onClick={() => handleCancel(e.schedule_name)}
                              disabled={isCancelling}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex flex-col items-center gap-1 text-xs text-ui-text2">
            <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <Pagination
              page={Math.floor(skip / limit)}
              totalPages={Math.ceil(total / limit)}
              onPageChange={(p) => setSkip(p * limit)}
              previousLabel="Prev"
              className="pt-0"
            />
          </div>
        )}

        {/* Detail panel */}
        {detail?.data && (
          <div className="card-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-ui-text">Trigger Detail</p>
              <IconButton label="Close" size="xs" onClick={() => setSelectedName(null)}>
                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z"/>
                </svg>
              </IconButton>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-ui-text2 md:grid-cols-3">
              <div><span className="font-medium">Schedule name:</span> {detail.data.schedule_name}</div>
              <div><span className="font-medium">Group:</span> {detail.data.group_name}</div>
              <div><span className="font-medium">Source ID:</span> {detail.data.source_id || '—'}</div>
              <div><span className="font-medium">Fired on:</span> {detail.data.fired_on ? new Date(detail.data.fired_on).toLocaleString() : '—'}</div>
              <div><span className="font-medium">Deleted on:</span> {detail.data.deleted_on ? new Date(detail.data.deleted_on).toLocaleString() : '—'}</div>
              <div><span className="font-medium">Updated:</span> {detail.data.updated_on ? new Date(detail.data.updated_on).toLocaleString() : '—'}</div>
            </div>
            {detail.data.failure_reason && (
              <p className="mt-2 rounded-chip bg-danger/10 px-3 py-2 font-mono text-xs text-danger-text">{detail.data.failure_reason}</p>
            )}
            {detail.data.payload && (
              <pre className="mt-3 overflow-x-auto rounded-chip bg-ui-subtle px-3 py-2 text-xs text-ui-text2">
                {JSON.stringify(detail.data.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
